import { useRef, useState, useEffect, useMemo, Suspense, forwardRef } from "react";
import { Canvas, useFrame, useThree, extend, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls as ThreeOrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { RotateCcw } from "lucide-react";
import { doorSegments, shelfLayoutDetailed, doorUnits } from "@/lib/pricing";

extend({ OrbitControls: ThreeOrbitControls });

const T = 0.03; // panel thickness (m)

const Controls = forwardRef((props, ref) => {
  const { camera, gl } = useThree();
  return (
    <orbitControls
      ref={ref}
      args={[camera, gl.domElement]}
      enablePan={false}
      enableDamping
      dampingFactor={0.08}
      minDistance={1.2}
      maxDistance={14}
      maxPolarAngle={Math.PI / 1.9}
    />
  );
});
Controls.displayName = "Controls";

function ControlsTicker({ controlsRef }) {
  useFrame(() => controlsRef.current?.update());
  return null;
}

function Panel({ size, position, color, highlight }) {
  return (
    <mesh position={position} castShadow receiveShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial
        color={color}
        roughness={0.65}
        metalness={0.05}
        emissive={highlight ? "#AE7439" : "#000000"}
        emissiveIntensity={highlight ? 0.55 : 0}
      />
    </mesh>
  );
}

function DoorMesh({ width, height, color, gloss, textureUrl, meshX, onClick, highlight }) {
  const tex = useLoader(THREE.TextureLoader, textureUrl);
  const mapTex = useMemo(() => {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(2, 3);
    tex.anisotropy = 4;
    return tex;
  }, [tex]);
  return (
    <mesh
      position={[meshX, 0, 0]}
      castShadow
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        document.body.style.cursor = "auto";
      }}
    >
      <boxGeometry args={[width - 0.012, height, T]} />
      <meshStandardMaterial
        color={color}
        roughness={gloss ?? 0.5}
        metalness={0.08}
        bumpMap={mapTex}
        bumpScale={0.003}
        roughnessMap={mapTex}
        emissive={highlight ? "#AE7439" : "#000000"}
        emissiveIntensity={highlight ? 0.5 : 0}
      />
    </mesh>
  );
}

function PlainDoorMesh({ width, height, color, gloss, meshX, onClick, highlight }) {
  return (
    <mesh
      position={[meshX, 0, 0]}
      castShadow
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        document.body.style.cursor = "auto";
      }}
    >
      <boxGeometry args={[width - 0.012, height, T]} />
      <meshStandardMaterial
        color={color}
        roughness={gloss ?? 0.5}
        metalness={0.08}
        emissive={highlight ? "#AE7439" : "#000000"}
        emissiveIntensity={highlight ? 0.5 : 0}
      />
    </mesh>
  );
}

function GlassDoorMesh({ width, height, tint, opacity, meshX, onClick, highlight }) {
  const w = width - 0.012;
  const fr = 0.045; // alüminyum çerçeve kalınlığı (m)
  const handlers = {
    onClick: (e) => {
      e.stopPropagation();
      onClick();
    },
    onPointerOver: (e) => {
      e.stopPropagation();
      document.body.style.cursor = "pointer";
    },
    onPointerOut: () => {
      document.body.style.cursor = "auto";
    },
  };
  const frameMat = (
    <meshStandardMaterial
      color="#161616"
      metalness={0.75}
      roughness={0.3}
      emissive={highlight ? "#AE7439" : "#000000"}
      emissiveIntensity={highlight ? 0.6 : 0}
    />
  );
  return (
    <group position={[meshX, 0, 0]}>
      {/* Cam panel */}
      <mesh castShadow {...handlers}>
        <boxGeometry args={[w, height, T * 0.35]} />
        <meshPhysicalMaterial color={tint} transparent opacity={opacity} roughness={0.08} metalness={0.1} />
      </mesh>
      {/* Siyah alüminyum çerçeve */}
      <mesh position={[0, height / 2 - fr / 2, 0]} castShadow {...handlers}>
        <boxGeometry args={[w, fr, T]} />
        {frameMat}
      </mesh>
      <mesh position={[0, -height / 2 + fr / 2, 0]} castShadow {...handlers}>
        <boxGeometry args={[w, fr, T]} />
        {frameMat}
      </mesh>
      <mesh position={[-w / 2 + fr / 2, 0, 0]} castShadow {...handlers}>
        <boxGeometry args={[fr, height, T]} />
        {frameMat}
      </mesh>
      <mesh position={[w / 2 - fr / 2, 0, 0]} castShadow {...handlers}>
        <boxGeometry args={[fr, height, T]} />
        {frameMat}
      </mesh>
    </group>
  );
}

function TexturedPanel({ size, position, mapUrl, receiveShadow = true, highlight }) {
  const tex = useLoader(THREE.TextureLoader, mapUrl);
  const map = useMemo(() => {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = 4;
    return tex;
  }, [tex]);
  return (
    <mesh position={position} castShadow receiveShadow={receiveShadow}>
      <boxGeometry args={size} />
      <meshStandardMaterial
        map={map}
        color="#ffffff"
        roughness={0.6}
        metalness={0.04}
        emissive={highlight ? "#AE7439" : "#000000"}
        emissiveIntensity={highlight ? 0.5 : 0}
      />
    </mesh>
  );
}

function BodyPanel({ size, position, color, mapUrl, highlight, onClick }) {
  const clickProps = onClick
    ? {
        onClick: (e) => {
          e.stopPropagation();
          onClick();
        },
        onPointerOver: (e) => {
          e.stopPropagation();
          document.body.style.cursor = "pointer";
        },
        onPointerOut: () => {
          document.body.style.cursor = "auto";
        },
      }
    : {};
  if (mapUrl) {
    return (
      <Suspense fallback={<Panel size={size} position={position} color={color} highlight={highlight} />}>
        <group {...clickProps}>
          <TexturedPanel size={size} position={position} mapUrl={mapUrl} highlight={highlight} />
        </group>
      </Suspense>
    );
  }
  return (
    <group {...clickProps}>
      <Panel size={size} position={position} color={color} highlight={highlight} />
    </group>
  );
}

function WoodDoorMesh({ width, height, color, gloss, colorMapUrl, meshX, onClick, highlight }) {
  const tex = useLoader(THREE.TextureLoader, colorMapUrl);
  const map = useMemo(() => {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = 4;
    return tex;
  }, [tex]);
  return (
    <mesh
      position={[meshX, 0, 0]}
      castShadow
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        document.body.style.cursor = "auto";
      }}
    >
      <boxGeometry args={[width - 0.012, height, T]} />
      <meshStandardMaterial
        map={map}
        color="#ffffff"
        roughness={gloss ?? 0.55}
        metalness={0.06}
        emissive={highlight ? "#AE7439" : "#000000"}
        emissiveIntensity={highlight ? 0.5 : 0}
      />
    </mesh>
  );
}

function MirrorDoorMesh({ width, height, meshX, onClick, highlight }) {
  const { gl } = useThree();
  // Gerçek ayna görünümü: ortam yansıması yalnızca ayna yüzeyine uygulanır
  const envTex = useMemo(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const tex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
    return tex;
  }, [gl]);
  useEffect(() => () => envTex.dispose(), [envTex]);
  return (
    <mesh
      position={[meshX, 0, 0]}
      castShadow
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        document.body.style.cursor = "auto";
      }}
    >
      <boxGeometry args={[width - 0.012, height, T]} />
      <meshPhysicalMaterial
        color="#f4f7f9"
        metalness={1}
        roughness={0.03}
        envMap={envTex}
        envMapIntensity={1.15}
        clearcoat={0.6}
        clearcoatRoughness={0.05}
        emissive={highlight ? "#AE7439" : "#000000"}
        emissiveIntensity={highlight ? 0.25 : 0}
      />
    </mesh>
  );
}

function Door({ width, height, depthZ, hingeX, hingeSide, color, gloss, textureUrl, colorMapUrl, open, onClick, y = 0, glass = null, highlight = false, mirror = false }) {
  const ref = useRef();
  const target = open ? (hingeSide === "left" ? -Math.PI * 0.62 : Math.PI * 0.62) : 0;
  useFrame(() => {
    if (!ref.current) return;
    ref.current.rotation.y += (target - ref.current.rotation.y) * 0.12;
  });
  const meshX = hingeSide === "left" ? width / 2 : -width / 2;
  const knobX = hingeSide === "left" ? width - 0.05 : -width + 0.05;
  const doorProps = { width, height, color, gloss, meshX, onClick, highlight };
  return (
    <group ref={ref} position={[hingeX, y, depthZ]}>
      {glass ? (
        <GlassDoorMesh
          width={width}
          height={height}
          tint={glass.tint}
          opacity={glass.opacity}
          meshX={meshX}
          onClick={onClick}
          highlight={highlight}
        />
      ) : mirror ? (
        <MirrorDoorMesh width={width} height={height} meshX={meshX} onClick={onClick} highlight={highlight} />
      ) : (
        <Suspense fallback={<PlainDoorMesh {...doorProps} />}>
          {colorMapUrl ? (
            <WoodDoorMesh {...doorProps} colorMapUrl={colorMapUrl} />
          ) : textureUrl ? (
            <DoorMesh {...doorProps} textureUrl={textureUrl} />
          ) : (
            <PlainDoorMesh {...doorProps} />
          )}
        </Suspense>
      )}
      {!mirror && (
        <mesh position={[knobX, 0, 0.03]} castShadow>
          <cylinderGeometry args={[0.012, 0.012, 0.12, 16]} />
          <meshStandardMaterial color="#7a7a7a" metalness={0.9} roughness={0.25} />
        </mesh>
      )}
    </group>
  );
}

function FlapDoor({ width, height, depthZ, x, bottomY, color, gloss, textureUrl, colorMapUrl, open, onClick, glass = null, highlight = false, mirror = false, noHandle = false }) {
  const ref = useRef();
  const target = open ? Math.PI * 0.52 : 0; // düşer kapak: alt kenardan aşağı/dışarı açılır
  useFrame(() => {
    if (!ref.current) return;
    ref.current.rotation.x += (target - ref.current.rotation.x) * 0.12;
  });
  const doorProps = { width, height, color, gloss, meshX: 0, onClick, highlight };
  return (
    <group position={[x, bottomY, depthZ]}>
      <group ref={ref}>
        <group position={[0, height / 2, 0]}>
          {glass ? (
            <GlassDoorMesh
              width={width}
              height={height}
              tint={glass.tint}
              opacity={glass.opacity}
              meshX={0}
              onClick={onClick}
              highlight={highlight}
            />
          ) : mirror ? (
            <MirrorDoorMesh width={width} height={height} meshX={0} onClick={onClick} highlight={highlight} />
          ) : (
            <Suspense fallback={<PlainDoorMesh {...doorProps} />}>
              {colorMapUrl ? (
                <WoodDoorMesh {...doorProps} colorMapUrl={colorMapUrl} />
              ) : textureUrl ? (
                <DoorMesh {...doorProps} textureUrl={textureUrl} />
              ) : (
                <PlainDoorMesh {...doorProps} />
              )}
            </Suspense>
          )}
          {/* Kulp — kapağın üst kenarında, yatay (ayna ve bas-aç kapaklar kulpsuzdur) */}
          {!mirror && !noHandle && (
            <mesh position={[0, height / 2 - 0.05, 0.03]} castShadow>
              <boxGeometry args={[Math.min(width * 0.3, 0.2), 0.016, 0.016]} />
              <meshStandardMaterial color="#7a7a7a" metalness={0.9} roughness={0.25} />
            </mesh>
          )}
        </group>
      </group>
    </group>
  );
}

function Drawer({ width, x, y, zFront, depth, boxDepth, frontH, frontColor, boxColor, open, visible, onClick }) {
  const ref = useRef();
  const target = open ? boxDepth + 0.08 : 0;
  useFrame(() => {
    if (ref.current) ref.current.position.z += (target - ref.current.position.z) * 0.12;
  });
  const wallH = frontH - 0.06; // box wall height (rail clearance)
  const boxCenterY = -frontH / 2 + wallH / 2 + 0.008;
  const hoverOn = (e) => {
    e.stopPropagation();
    document.body.style.cursor = "pointer";
  };
  const hoverOff = () => {
    document.body.style.cursor = "auto";
  };
  return (
    <group ref={ref} position={[x, y, 0]}>
      {/* Front face (clickable) */}
      <mesh
        position={[0, 0, zFront]}
        castShadow
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onPointerOver={hoverOn}
        onPointerOut={hoverOff}
      >
        <boxGeometry args={[width, frontH, T]} />
        <meshStandardMaterial color={frontColor} roughness={0.6} metalness={0.05} />
      </mesh>
      {/* Handle — only visible when doors are open */}
      {visible && (
        <mesh position={[0, frontH / 2 - 0.035, zFront + T / 2 + 0.012]} castShadow>
          <boxGeometry args={[Math.min(width * 0.32, 0.24), 0.016, 0.016]} />
          <meshStandardMaterial color="#7a7a7a" metalness={0.9} roughness={0.25} />
        </mesh>
      )}
      {/* Drawer box: bottom + sides + back */}
      <mesh position={[0, boxCenterY - wallH / 2 - 0.006, zFront - T / 2 - boxDepth / 2]}>
        <boxGeometry args={[width - 0.03, 0.012, boxDepth]} />
        <meshStandardMaterial color={boxColor} roughness={0.7} />
      </mesh>
      <mesh position={[-width / 2 + 0.015, boxCenterY, zFront - T / 2 - boxDepth / 2]}>
        <boxGeometry args={[0.012, wallH, boxDepth]} />
        <meshStandardMaterial color={boxColor} roughness={0.7} />
      </mesh>
      <mesh position={[width / 2 - 0.015, boxCenterY, zFront - T / 2 - boxDepth / 2]}>
        <boxGeometry args={[0.012, wallH, boxDepth]} />
        <meshStandardMaterial color={boxColor} roughness={0.7} />
      </mesh>
      <mesh position={[0, boxCenterY, zFront - T / 2 - boxDepth + 0.006]}>
        <boxGeometry args={[width - 0.03, wallH, 0.012]} />
        <meshStandardMaterial color={boxColor} roughness={0.7} />
      </mesh>
    </group>
  );
}

function Wardrobe({ config, doorOpen, onToggleDoor, drawerOpen, onToggleDrawer, onSelectDoor, onSelectShelf, selected }) {
  const w = config.width / 100;
  const h = config.height / 100;
  const d = config.depth / 100;
  const bodyColor = config.bodyHex;
  const doorColor = config.doorHex;
  // Cam kapak: siyah alüminyum çerçeve + renkli cam (şeffaf / füme açık / füme koyu)
  const glassInfo =
    config.doorMaterial === "cam"
      ? {
          tint: doorColor,
          opacity:
            config.doorColor === "cam_fume_koyu"
              ? 0.7
              : config.doorColor === "cam_fume_acik"
                ? 0.45
                : 0.22,
        }
      : null;
  const interior = "#efe7db";
  const isMirror = config.doorMaterial === "ayna";
  const noDoorsLeft = !config.doorless && doorUnits(config).length === 0;

  // Base: legs (10cm) or plinth/alt kapama (8cm) or none
  const baseH = config.base === "ayak" ? 0.1 : config.base === "alt_kapama" ? 0.08 : 0;
  const drawers = config.drawers ?? 0;

  // Shelves start above the drawer section (+ its fixed cover); equal spacing
  const drawerFrontH = (config.drawerHeight ?? 20) / 100;
  const drawerRowH = drawerFrontH + 0.01;
  const drawerSectionH = drawers > 0 ? drawers * drawerRowH + T : 0;
  // Parçalı kapakta raflar kapak bitimlerine hizalanır (bölme rafları otomatik eklenir)
  const shelfDetailed = shelfLayoutDetailed(config);
  const removedShelves = new Set((config.removedShelves || []).map(String));

  const doors = config.doorless ? 0 : config.doors;
  const dw = doors > 0 ? w / doors : 0;
  const dividers = config.dividers ?? 0;
  const numCols = dividers + 1;
  const colW = (w - 2 * T - dividers * T) / numCols;

  return (
    <group position={[0, 0, 0]}>
      {/* Base elements on the floor */}
      {config.base === "ayak" &&
        [
          [-w / 2 + 0.05, -d / 2 + 0.05],
          [w / 2 - 0.05, -d / 2 + 0.05],
          [-w / 2 + 0.05, d / 2 - 0.05],
          [w / 2 - 0.05, d / 2 - 0.05],
        ].map(([lx, lz], i) => (
          <mesh key={i} position={[lx, -h / 2 + baseH / 2, lz]} castShadow>
            <cylinderGeometry args={[0.025, 0.03, baseH, 14]} />
            <meshStandardMaterial color="#8a8a8a" metalness={0.7} roughness={0.35} />
          </mesh>
        ))}
      {config.base === "alt_kapama" && (
        <>
          {/* Yan parçalar: gövde rengi, yan dikmelerin devamı */}
          <BodyPanel
            size={[T, baseH, d]}
            position={[-w / 2 + T / 2, -h / 2 + baseH / 2, 0]}
            color={bodyColor}
            mapUrl={config.bodyColorTex}
          />
          <BodyPanel
            size={[T, baseH, d]}
            position={[w / 2 - T / 2, -h / 2 + baseH / 2, 0]}
            color={bodyColor}
            mapUrl={config.bodyColorTex}
          />
          {/* Ön orta parça: kapak rengi (cam/ayna kapakta gövde rengi/malzemesi) */}
          <BodyPanel
            size={[w - 2 * T, baseH, T]}
            position={[0, -h / 2 + baseH / 2, d / 2 - T / 2]}
            color={config.doorMaterial === "cam" || config.doorMaterial === "ayna" ? bodyColor : doorColor}
            mapUrl={config.doorMaterial === "cam" || config.doorMaterial === "ayna" ? config.bodyColorTex : config.doorColorTex}
          />
        </>
      )}

      {/* Carcass elevated by baseH */}
      <group position={[0, baseH, 0]}>
        {!(config.noBackPanel && config.doorless) && (
          <Panel size={[w - 2 * T, h - 2 * T, T]} position={[0, 0, -d / 2 + T / 2]} color={interior} />
        )}
        <BodyPanel size={[T, h, d]} position={[-w / 2 + T / 2, 0, 0]} color={bodyColor} mapUrl={config.bodyColorTex} />
        <BodyPanel size={[T, h, d]} position={[w / 2 - T / 2, 0, 0]} color={bodyColor} mapUrl={config.bodyColorTex} />
        <BodyPanel size={[w, T, d]} position={[0, h / 2 - T / 2, 0]} color={bodyColor} mapUrl={config.bodyColorTex} />
        <BodyPanel size={[w, T, d]} position={[0, -h / 2 + T / 2, 0]} color={bodyColor} mapUrl={config.bodyColorTex} />
        {dividers > 0 &&
          Array.from({ length: dividers }).map((_, i) => (
            <BodyPanel
              key={i}
              size={[T, h - 2 * T, d - T]}
              position={[-w / 2 + (w * (i + 1)) / (dividers + 1), 0, T / 2]}
              color={bodyColor}
              mapUrl={config.bodyColorTex}
            />
          ))}
        {shelfDetailed.map((s, i) => {
          const yPos = -h / 2 + T + s.y / 100;
          // Raflar bölme bazında: her bölmenin rafı ayrı parça, bağımsız kaldırılabilir
          return Array.from({ length: numCols }).map((_, j) => {
            const key = `${i}:${j}`;
            if (!s.structural && removedShelves.has(key)) return null;
            const isSel = selected?.type === "shelf" && selected.key === key;
            return (
              <BodyPanel
                key={key}
                size={[colW, T, d - 2 * T]}
                position={[-w / 2 + T + j * (colW + T) + colW / 2, yPos, T / 2]}
                color={bodyColor}
                mapUrl={config.bodyColorTex}
                highlight={isSel}
                onClick={s.structural ? undefined : () => onSelectShelf?.(i, j, key)}
              />
            );
          });
        })}
        {/* Fixed drawer top covers (raf gibi) — drawer slides under, top stays closed */}
        {Array.from({ length: drawers }).map((_, i) =>
          Array.from({ length: numCols }).map((_, j) => (
            <BodyPanel
              key={`cov-${i}-${j}`}
              size={[colW, T, d - 2 * T]}
              position={[
                -w / 2 + T + j * (colW + T) + colW / 2,
                -h / 2 + T + (i + 1) * drawerRowH + 0.006,
                T / 2,
              ]}
              color={bodyColor}
              mapUrl={config.bodyColorTex}
            />
          ))
        )}
        {/* Drawers — per compartment (orta dikme başına), each slides independently */}
        {Array.from({ length: drawers }).map((_, i) =>
          Array.from({ length: numCols }).map((_, j) => {
            const idx = i * numCols + j;
            return (
              <Drawer
                key={idx}
                width={colW - 0.012}
                x={-w / 2 + T + j * (colW + T) + colW / 2}
                y={-h / 2 + T + drawerFrontH / 2 + i * drawerRowH}
                zFront={d / 2 - T - 0.002}
                depth={d}
                boxDepth={d - 0.08}
                frontH={drawerFrontH}
                frontColor={bodyColor}
                boxColor={interior}
                open={drawerOpen[idx]}
                visible={config.doorless || noDoorsLeft ? true : doorOpen.some(Boolean)}
                onClick={() => onToggleDrawer(idx)}
              />
            );
          })
        )}
        {Array.from({ length: doors }).map((_, i) => {
          const removedDoors = new Set(config.removedDoors || []);
          if (config.doorStyle === "flap") {
            const key = `${i}`;
            if (removedDoors.has(key)) return null;
            const isSel = selected?.type === "door" && selected.key === key;
            return (
              <FlapDoor
                key={i}
                width={dw}
                height={h - 0.01}
                depthZ={d / 2 - T / 2}
                x={-w / 2 + dw * i + dw / 2}
                bottomY={-h / 2 + 0.005}
                color={doorColor}
                gloss={config.doorGloss}
                textureUrl={config.doorTexture}
                colorMapUrl={config.doorColorTex}
                glass={glassInfo}
                mirror={isMirror}
                noHandle={config.flapHandle === "push"}
                open={doorOpen[i]}
                highlight={isSel}
                onClick={() => {
                  onToggleDoor(i);
                  onSelectDoor?.(key, i + 1, "");
                }}
              />
            );
          }
          // Kapaklar çiftler halinde dönüşümlü menteşelenir: sol, sağ, sol, sağ
          const hingeSide = i % 2 === 1 ? "right" : "left";
          const hingeX = hingeSide === "left" ? -w / 2 + dw * i : -w / 2 + dw * (i + 1);
          const common = {
            width: dw,
            depthZ: d / 2 - T / 2,
            hingeX,
            hingeSide,
            color: doorColor,
            gloss: config.doorGloss,
            textureUrl: config.doorTexture,
            colorMapUrl: config.doorColorTex,
            glass: glassInfo,
            mirror: isMirror,
          };
          if (config.doorLayout === "split" || config.doorLayout === "split3") {
            // Parçalı kapak: parçalar bağımsız açılır (alttan üste dizilir)
            const segsCm = doorSegments(config.height, config.doorLayout);
            let yCursor = -h / 2 + 0.005;
            return (
              <group key={i}>
                {segsCm.map((sg, k) => {
                  const segH = sg.h / 100;
                  const y = yCursor + segH / 2;
                  yCursor += segH;
                  const key = `${i}:${k}`;
                  if (removedDoors.has(key)) return null;
                  const isSel = selected?.type === "door" && selected.key === key;
                  return (
                    <Door
                      key={k}
                      {...common}
                      height={segH}
                      y={y}
                      open={doorOpen[i * segsCm.length + k]}
                      highlight={isSel}
                      onClick={() => {
                        onToggleDoor(i * segsCm.length + k);
                        onSelectDoor?.(key, i + 1, sg.suffix);
                      }}
                    />
                  );
                })}
              </group>
            );
          }
          const key = `${i}`;
          if (removedDoors.has(key)) return null;
          const isSel = selected?.type === "door" && selected.key === key;
          return (
            <Door
              key={i}
              height={h - 0.01}
              open={doorOpen[i]}
              highlight={isSel}
              onClick={() => {
                onToggleDoor(i);
                onSelectDoor?.(key, i + 1, "");
              }}
              {...common}
            />
          );
        })}
      </group>
    </group>
  );
}

export default function Wardrobe3D({ config, openAllSignal, onSelectDoor, onSelectShelf, selected, dpr, captureMode = false }) {
  const controls = useRef();
  const segFactor =
    config.doorStyle === "flap" ? 1 : config.doorLayout === "split3" ? 3 : config.doorLayout === "split" ? 2 : 1;
  const doorCount = (config.doorless ? 0 : config.doors) * segFactor;
  const [doorOpen, setDoorOpen] = useState(() => Array(doorCount).fill(false));
  const [drawerOpen, setDrawerOpen] = useState(() => Array((config.drawers ?? 0) * ((config.dividers ?? 0) + 1)).fill(false));

  useEffect(() => {
    setDoorOpen(Array(doorCount).fill(false));
  }, [doorCount]);

  useEffect(() => {
    setDrawerOpen(Array((config.drawers ?? 0) * ((config.dividers ?? 0) + 1)).fill(false));
  }, [config.drawers, config.dividers]);

  useEffect(() => {
    if (openAllSignal === undefined) return;
    setDoorOpen(Array(doorCount).fill(openAllSignal));
    // Kapaklar toplu kapatıldığında açık çekmeceler de yuvasına döner
    if (!openAllSignal) setDrawerOpen((d) => d.map(() => false));
  }, [openAllSignal, doorCount]);

  const toggleDoor = (i) =>
    setDoorOpen((prev) => {
      const next = prev.map((v, idx) => (idx === i ? !v : v));
      // Son açık kapak da kapanınca çekmeceler otomatik içeri çekilir
      if (!next.some(Boolean)) setDrawerOpen((d) => d.map(() => false));
      return next;
    });

  const toggleDrawer = (i) =>
    setDrawerOpen((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const maxDim = Math.max(config.width, config.height, config.depth) / 100;
  const camDist = maxDim * 2.2 + 1;
  // Yakalama modunda (PDF için) kamera modele göre çerçevelenir: model kareyi doldurur
  let camPosition = [camDist * 0.85, camDist * 0.55, camDist];
  if (captureMode) {
    const fovRad = (42 * Math.PI) / 180;
    const vHalf = Math.tan(fovRad / 2);
    const hHalf = Math.atan(vHalf * 2); // yakalama çerçevesi ~1200x600 (2:1)
    const mW = config.width / 100;
    const mH = config.height / 100;
    const fitDist = Math.max((mH * 1.3) / (2 * vHalf), (mW * 1.25) / (2 * Math.tan(hHalf)));
    const dirLen = Math.hypot(0.85, 0.5, 1);
    camPosition = [0.85, 0.5, 1].map((v) => (v / dirLen) * fitDist);
  }

  return (
    <div
      className="relative w-full h-[460px] lg:h-[600px] rounded-2xl overflow-hidden border border-stone-800 shadow-2xl bg-stone-900"
      data-doors-open={doorOpen.filter(Boolean).length}
      data-drawers-open={drawerOpen.filter(Boolean).length}
    >
      <Canvas shadows gl={{ preserveDrawingBuffer: true }} dpr={dpr} camera={{ position: camPosition, fov: 42 }} data-testid="wardrobe-3d-canvas">
        <color attach="background" args={["#1c1917"]} />
        <ambientLight intensity={0.7} />
        <hemisphereLight args={["#ffffff", "#2a2a2a", 0.4]} />
        <directionalLight position={[5, 8, 5]} intensity={1.2} castShadow shadow-mapSize={[1024, 1024]} />
        <directionalLight position={[-5, 4, -3]} intensity={0.5} />
        <Suspense fallback={null}>
          <Wardrobe config={config} doorOpen={doorOpen} onToggleDoor={toggleDoor} drawerOpen={drawerOpen} onToggleDrawer={toggleDrawer} onSelectDoor={onSelectDoor} onSelectShelf={onSelectShelf} selected={selected} />
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -config.height / 200 - 0.01, 0]} receiveShadow>
            <planeGeometry args={[10, 10]} />
            <shadowMaterial opacity={0.35} />
          </mesh>
        </Suspense>
        <Controls ref={controls} />
        <ControlsTicker controlsRef={controls} />
      </Canvas>

      <button
        data-testid="designer-reset-view-btn"
        onClick={() => controls.current?.reset()}
        className="absolute top-4 right-4 flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-medium px-3 py-2 hover:bg-white/20 transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Görünümü Sıfırla
      </button>
      <div className="absolute bottom-4 left-4 text-white/60 text-xs font-mono">
        Döndürmek için sürükleyin • Kapaklara ve çekmecelere tıklayın
      </div>
    </div>
  );
}
